import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const PROGRAMS = [
  // Indian Classical
  'Bharatanatyam', 'Kathak', 'Kuchipudi', 'Odissi', 'Mohiniyattam',
  'Manipuri', 'Sattriya', 'Kathakali',
  // Indian Semi-Classical & Folk
  'Semi-Classical', 'Folk', 'Bollywood', 'Garba/Dandiya', 'Bhangra', 'Lavani',
  // Indian Fusion
  'Fusion', 'Indo-Contemporary',
  // Western & Latin
  'Ballet', 'Contemporary', 'Jazz', 'Hip-Hop', 'Salsa', 'Bachata',
  'Flamenco', 'Tap',
  // Fitness & Wellness
  'Conditioning', 'Yoga', 'Pilates',
  // Other
  'Other'
];

const COUNTRIES = [
  { name: 'United States', code: '+1', maxDigits: 10 },
  { name: 'India', code: '+91', maxDigits: 10 },
  { name: 'Canada', code: '+1', maxDigits: 10 },
  { name: 'United Kingdom', code: '+44', maxDigits: 10 },
  { name: 'Australia', code: '+61', maxDigits: 9 },
  { name: 'Singapore', code: '+65', maxDigits: 8 },
  { name: 'Malaysia', code: '+60', maxDigits: 10 },
  { name: 'UAE', code: '+971', maxDigits: 9 },
  { name: 'Germany', code: '+49', maxDigits: 11 },
  { name: 'France', code: '+33', maxDigits: 9 },
  { name: 'Netherlands', code: '+31', maxDigits: 9 },
  { name: 'South Africa', code: '+27', maxDigits: 9 },
  { name: 'New Zealand', code: '+64', maxDigits: 9 },
  { name: 'Sri Lanka', code: '+94', maxDigits: 9 },
  { name: 'Nepal', code: '+977', maxDigits: 10 },
];

const STATES_BY_COUNTRY = {
  'United States': [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC'
  ],
  'India': [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa',
    'Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala',
    'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland',
    'Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
    'Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Chandigarh','Puducherry'
  ],
  'Canada': [
    'AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'
  ],
  'United Kingdom': [
    'England','Scotland','Wales','Northern Ireland'
  ],
  'Australia': [
    'ACT','NSW','NT','QLD','SA','TAS','VIC','WA'
  ],
};

const StudioWizard = () => {
  const navigate = useNavigate();
  const { refreshUser, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [noWebsite, setNoWebsite] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    danceStyles: [],
    description: '',
    location: { address: '', city: '', state: '', country: 'United States' },
    contact: { phone: '', email: '', website: '' },
    emailSettings: { replyTo: '' }
  });

  const selectedCountry = useMemo(
    () => COUNTRIES.find(c => c.name === formData.location.country) || COUNTRIES[0],
    [formData.location.country]
  );

  const statesForCountry = STATES_BY_COUNTRY[formData.location.country] || [];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNested = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleCountryChange = (country) => {
    setFormData(prev => ({
      ...prev,
      location: { ...prev.location, country, state: '' },
      contact: { ...prev.contact, phone: '' }
    }));
  };

  const handlePhoneChange = (value) => {
    // Only allow digits
    const digits = value.replace(/\D/g, '');
    if (digits.length <= selectedCountry.maxDigits) {
      updateNested('contact', 'phone', digits);
    }
  };

  const formatPhone = (digits) => {
    if (!digits) return '';
    // US/Canada formatting: (xxx) xxx-xxxx
    if (selectedCountry.maxDigits === 10 && (selectedCountry.code === '+1')) {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    }
    // India: xxxxx xxxxx
    if (selectedCountry.name === 'India') {
      if (digits.length <= 5) return digits;
      return `${digits.slice(0,5)} ${digits.slice(5)}`;
    }
    return digits;
  };

  const toggleStyle = (style) => {
    setFormData(prev => ({
      ...prev,
      danceStyles: prev.danceStyles.includes(style)
        ? prev.danceStyles.filter(s => s !== style)
        : [...prev.danceStyles, style]
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const fullPhone = formData.contact.phone
        ? `${selectedCountry.code}${formData.contact.phone}`
        : '';

      const payload = {
        ...formData,
        contact: { ...formData.contact, phone: fullPhone },
        emailSettings: {
          replyTo: formData.emailSettings?.replyTo || ''
        }
      };
      await api.post('/studio', payload);
      await api.patch('/studio/activate');
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create studio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-6">
        <div className="flex justify-end mb-4">
          <button
            onClick={logout}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Log out
          </button>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <h2 className="text-2xl font-bold mb-6">Tell us about your studio</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Studio Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => updateField('name', e.target.value)}
                placeholder="Enter your studio name"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Programs You Offer *</label>
              <div className="flex flex-wrap gap-2">
                {PROGRAMS.map(program => (
                  <button
                    key={program}
                    onClick={() => toggleStyle(program)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      formData.danceStyles.includes(program)
                        ? 'bg-brand-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {program}
                  </button>
                ))}
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select
                value={formData.location.country}
                onChange={e => handleCountryChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              >
                {COUNTRIES.map(c => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* State + City */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.location.country === 'India' ? 'State' : 'State / Province'}
                </label>
                {statesForCountry.length > 0 ? (
                  <select
                    value={formData.location.state}
                    onChange={e => updateNested('location', 'state', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  >
                    <option value="">Select your state</option>
                    {statesForCountry.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.location.state}
                    onChange={e => updateNested('location', 'state', e.target.value)}
                    placeholder="Enter your state"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.location.city}
                  onChange={e => updateNested('location', 'city', e.target.value)}
                  placeholder="Enter your city"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
            </div>

            {/* Phone with country code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <div className="flex">
                <div className="flex items-center px-3 bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-600 font-medium select-none">
                  {selectedCountry.code}
                </div>
                <input
                  type="tel"
                  value={formatPhone(formData.contact.phone)}
                  onChange={e => handlePhoneChange(e.target.value)}
                  placeholder="Enter your phone number"
                  maxLength={selectedCountry.maxDigits + 5}
                  className="flex-1 border border-gray-300 rounded-r-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
              <span className="text-xs text-gray-400 mt-1 block">
                {formData.contact.phone.length}/{selectedCountry.maxDigits} digits
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input
                type="url"
                value={formData.contact.website}
                onChange={e => updateNested('contact', 'website', e.target.value)}
                placeholder="Enter your website URL"
                disabled={noWebsite}
                className={`w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent ${noWebsite ? 'bg-gray-50 text-gray-400' : ''}`}
              />
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={noWebsite}
                  onChange={e => {
                    setNoWebsite(e.target.checked);
                    if (e.target.checked) updateNested('contact', 'website', '');
                  }}
                  className="rounded border-gray-300 text-brand-500 focus:ring-brand-400"
                />
                <span className="text-sm text-gray-500">I don't have a website yet</span>
              </label>
            </div>

            {/* Notification Email Preview */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notification Email</label>
              <p className="text-sm text-gray-900 font-mono">
                {formData.name
                  ? formData.name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim() + '@mydancehall.com'
                  : 'yourstudio@mydancehall.com'
                }
              </p>
              <p className="text-xs text-gray-400 mt-1">Students will receive reminders and notifications from this address. You can change this later in Settings.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reply-to Email (optional)</label>
              <input
                type="email"
                value={formData.emailSettings.replyTo}
                onChange={e => setFormData(prev => ({
                  ...prev,
                  emailSettings: { ...prev.emailSettings, replyTo: e.target.value }
                }))}
                placeholder="Enter your email address"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">When students reply to notifications, replies go to this address</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">About your studio (optional)</label>
              <textarea
                value={formData.description}
                onChange={e => updateField('description', e.target.value)}
                placeholder="Enter a description of your studio"
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={saving || !formData.name || formData.danceStyles.length === 0}
              className="bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Creating...' : 'Create Studio'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudioWizard;
